import Header from "@/components/header/header";
import { translate } from "@/config/translator";
import {
  formatDateFromLang,
  getDateFormatLocale,
  getFileFAIcon,
} from "@/config/utils";
import {
  Button,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  Tooltip,
} from "@fluentui/react-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { filesize } from "filesize";
import UploadDialog from "./uploadDialog";
import { TopBarMobile } from "@/components/topBarMobile/topBarMobile";
import {
  faEllipsis,
  faMessageLines,
  faPlus,
} from "@fortawesome/pro-regular-svg-icons";
import ListItemMobile from "@/components/listItemMobile/ListItemMobile";
import { formatDistanceToNow } from "date-fns";
import { FullScreenSpinner } from "@/components/fullScreenSpinner/FullScreenSpinner";

import { FullScreenPleaseSelectMandateNotice } from "@/components/fullScreenPleaseSelectMandateNotice/FullScreenPleaseSelectMandateNotice";
import { useUploadsList } from "./uploadsList/useUploadsList";
import FilePreviewDialog from "@/components/filePreviewDialog/filePreviewDialog";
import ConfirmationDialog from "@/components/confirmationDialog/ConfirmationDialog";
import PageSwitcher from "@/components/pageSwitcher/PageSwitcher";

export default function UploadsList() {
  const {
    isLoading,
    onDownload,
    onDelete,
    columns,
    files,
    uploadDialogOpen,
    setUploadDialogOpen,
    isTablet,
    navigate,
    setSearchQuery,
    selectedMandateId,
    searchQuery,
    lang,
    fileActionOpenId,
    setFileActionOpenId,
    deleteFileConfirmationOpenId,
    setDeleteFileConfirmationOpenId,
    onOpenPreview,
    fetchFilesHelper,
    currentFilePreview,
    setCurrentFilePreview,
    totalPages,
    page,
    setPage,
  } = useUploadsList();

  if (!selectedMandateId) {
    return <FullScreenPleaseSelectMandateNotice />;
  }

  if (!isTablet && isLoading) {
    return (
      <>
        <TopBarMobile
          header="upload"
          rightSide={
            <>
              <FontAwesomeIcon
                className="text-font-white"
                size="xl"
                icon={faPlus}
                onClick={() => navigate("/upload/new")}
              />
            </>
          }
        />
        <FullScreenSpinner />
      </>
    );
  }

  if (isTablet)
    return (
      <div className="p-10">
        <Header
          text="upload"
          mainButtonText="upload"
          onMainButtonClick={() => setUploadDialogOpen(true)}
          searchValue={searchQuery}
          onChangeSearch={setSearchQuery}
        />

        {isLoading ? (
          <div className="h-[calc(100vh-20rem)]">
            <FullScreenSpinner />
          </div>
        ) : (
          <Table className="table-fixed">
            <colgroup>
              {columns.map(({ className }) => (
                <col className={className} />
              ))}
            </colgroup>
            <TableHeader>
              <TableRow>
                {columns.map(({ key, label }) => (
                  <TableHeaderCell key={`uploads-webapp-table-header-${key}`}>
                    {label}
                  </TableHeaderCell>
                ))}
              </TableRow>
            </TableHeader>

            {/* TABLE BODY */}
            <TableBody>
              {files?.map((file, index) => (
                <TableRow
                  className={
                    index % 2 === 0 ? "bg-white" : "bg-canvas-secondary"
                  }
                  key={`uploads-webapp-table-row-${file.id}`}
                >
                  {/* FILE ICON */}
                  <TableCell>
                    <FontAwesomeIcon
                      size="xl"
                      className="text-font-secondary"
                      icon={getFileFAIcon(file.contentType)}
                    />
                  </TableCell>

                  {/* DOCUMENT TYPE */}
                  <TableCell>
                    <Text>
                      {file.documentType === "accounting"
                        ? translate("accounting", lang)
                        : file.documentType === "payroll_accounting"
                          ? translate("payrollAccounting", lang)
                          : translate("taxReturn", lang)}
                    </Text>
                  </TableCell>

                  {/* FILENAME */}
                  <TableCell>
                    <Text className="break-all line-clamp-3">
                      {file.originalFilename}
                    </Text>
                  </TableCell>

                  {/* CREATION DATE */}
                  <TableCell>
                    <Text>
                      {formatDateFromLang(new Date(file.createdAt), lang)}
                    </Text>
                  </TableCell>

                  {/* FILE SIZE */}
                  <TableCell>
                    <Text>{filesize(file.size)}</Text>
                  </TableCell>

                  {/* ACTION */}
                  <TableCell>
                    <div className="flex flex-row items-center">
                      <Popover
                        onOpenChange={(_, data) =>
                          data.open
                            ? setFileActionOpenId(file.id)
                            : setFileActionOpenId(null)
                        }
                        open={fileActionOpenId === file.id}
                      >
                        <PopoverTrigger disableButtonEnhancement>
                          <FontAwesomeIcon
                            icon={faEllipsis}
                            className="text-font-secondary text-3xl cursor-pointer"
                          />
                        </PopoverTrigger>

                        <PopoverSurface>
                          <div className="flex flex-col gap-2 w-50">
                            <h3 className="font-bold text-lg">
                              {translate("action", lang)}
                            </h3>
                            <Button
                              appearance="primary"
                              onClick={() => onDownload(file.id)}
                            >
                              {translate("download", lang)}
                            </Button>
                            <Button onClick={() => onOpenPreview(file.id)}>
                              {translate("preview", lang)}
                            </Button>
                            <Button
                              onClick={() => {
                                setDeleteFileConfirmationOpenId(file.id);
                                setFileActionOpenId(null);
                              }}
                            >
                              {translate("delete", lang)}
                            </Button>
                          </div>
                        </PopoverSurface>
                      </Popover>
                      {file.note && (
                        // NOTE
                        <Tooltip content={file.note} relationship="description">
                          <div className="p-2 cursor-help">
                            <FontAwesomeIcon
                              icon={faMessageLines}
                              className="text-lg text-font-secondary"
                            />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <div className="mt-3 w-full flex flex-row justify-end">
          <PageSwitcher
            currentPage={page}
            setCurrentPage={setPage}
            totalPages={totalPages}
          />
        </div>
        <UploadDialog
          fetchFiles={fetchFilesHelper}
          isOpen={uploadDialogOpen}
          setIsOpen={setUploadDialogOpen}
        />
        <FilePreviewDialog
          file={currentFilePreview}
          isOpen={!!currentFilePreview}
          onClose={() => setCurrentFilePreview(null)}
          onDownload={onDownload}
        />
        <ConfirmationDialog
          isOpen={!!deleteFileConfirmationOpenId}
          onCancel={() => setDeleteFileConfirmationOpenId(null)}
          onConfirm={onDelete}
          messageString={translate("deleteUploadConfirmationMessage", lang)}
          titleString={translate("areYouSure", lang)}
        />
      </div>
    );

  return (
    <>
      <TopBarMobile
        header="upload"
        rightSide={
          <>
            <FontAwesomeIcon
              className="text-font-white"
              size="xl"
              icon={faPlus}
              onClick={() => navigate("/upload/new")}
            />
          </>
        }
      />
      <div className="p-6">
        {files &&
          files?.map((file) => (
            <ListItemMobile
              icon={getFileFAIcon(file.contentType)}
              primaryText={file.originalFilename}
              key={`upload-list-element-${file.id}`}
              onClick={() => onDownload(file.id)}
              secondaryText={formatDistanceToNow(file.createdAt, {
                addSuffix: true,
                locale: getDateFormatLocale(lang),
              })}
            />
          ))}
      </div>
    </>
  );
}
